"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STORAGE_POLL_DELAY = void 0;
exports.selectSessionStoreStrategyType = selectSessionStoreStrategyType;
exports.getSessionStoreStrategy = getSessionStoreStrategy;
exports.startSessionStore = startSessionStore;
const timer_1 = require("../../tools/timer");
const observable_1 = require("../../tools/observable");
const timeUtils_1 = require("../../tools/utils/timeUtils");
const functionUtils_1 = require("../../tools/utils/functionUtils");
const stringUtils_1 = require("../../tools/utils/stringUtils");
const display_1 = require("../../tools/display");
const sessionInCookie_1 = require("./storeStrategies/sessionInCookie");
const sessionState_1 = require("./sessionState");
const sessionInLocalStorage_1 = require("./storeStrategies/sessionInLocalStorage");
const sessionStoreOperations_1 = require("./sessionStoreOperations");
const sessionConstants_1 = require("./sessionConstants");
/**
 * Every second, the storage will be polled to check for any change that can occur
 * to the session state in another browser tab, or another window.
 * This value has been determined from our previous cookie-only implementation.
 */
exports.STORAGE_POLL_DELAY = timeUtils_1.ONE_SECOND;
/**
 * Selects the correct session store strategy type based on the configuration and storage
 * availability.
 */
function selectSessionStoreStrategyType(initConfiguration) {
    switch (initConfiguration.sessionPersistence) {
        case sessionConstants_1.SessionPersistence.COOKIE:
            return (0, sessionInCookie_1.selectCookieStrategy)(initConfiguration);
        case sessionConstants_1.SessionPersistence.LOCAL_STORAGE:
            return (0, sessionInLocalStorage_1.selectLocalStorageStrategy)();
        case undefined: {
            let sessionStoreStrategyType = (0, sessionInCookie_1.selectCookieStrategy)(initConfiguration);
            if (!sessionStoreStrategyType && initConfiguration.allowFallbackToLocalStorage) {
                sessionStoreStrategyType = (0, sessionInLocalStorage_1.selectLocalStorageStrategy)();
            }
            return sessionStoreStrategyType;
        }
        default:
            display_1.display.error(`Invalid session persistence '${String(initConfiguration.sessionPersistence)}'`);
    }
}
function getSessionStoreStrategy(sessionStoreStrategyType, configuration) {
    return sessionStoreStrategyType.type === sessionConstants_1.SessionPersistence.COOKIE
        ? (0, sessionInCookie_1.initCookieStrategy)(configuration, sessionStoreStrategyType.cookieOptions)
        : (0, sessionInLocalStorage_1.initLocalStorageStrategy)(configuration);
}
/**
 * Different session concepts:
 * - tracked, the session has an id and is updated along the user navigation
 * - not tracked, the session does not have an id but it is updated along the user navigation
 * - inactive, no session in store or session expired, waiting for a renew session
 */
function startSessionStore(sessionStoreStrategyType, configuration, productKey, computeTrackingType, sessionStoreStrategy = getSessionStoreStrategy(sessionStoreStrategyType, configuration)) {
    const renewObservable = new observable_1.Observable();
    const expireObservable = new observable_1.Observable();
    const sessionStateUpdateObservable = new observable_1.Observable();
    const watchSessionTimeoutId = (0, timer_1.setInterval)(watchSession, exports.STORAGE_POLL_DELAY);
    let sessionCache;
    startSession();
    const { throttled: throttledExpandOrRenewSession, cancel: cancelExpandOrRenewSession } = (0, functionUtils_1.throttle)(() => {
        (0, sessionStoreOperations_1.processSessionStoreOperations)({
            process: (sessionState) => {
                if ((0, sessionState_1.isSessionInNotStartedState)(sessionState)) {
                    return;
                }
                const synchronizedSession = synchronizeSession(sessionState);
                expandOrRenewSessionState(synchronizedSession);
                return synchronizedSession;
            },
            after: (sessionState) => {
                if ((0, sessionState_1.isSessionStarted)(sessionState) && !hasSessionInCache()) {
                    renewSessionInCache(sessionState);
                }
                sessionCache = sessionState;
            },
        }, sessionStoreStrategy);
    }, exports.STORAGE_POLL_DELAY);
    function expandSession() {
        (0, sessionStoreOperations_1.processSessionStoreOperations)({
            process: (sessionState) => (hasSessionInCache() ? synchronizeSession(sessionState) : undefined),
        }, sessionStoreStrategy);
    }
    /**
     * allows two behaviors:
     * - if the session is active, synchronize the session cache without updating the session store
     * - if the session is not active, clear the session store and expire the session cache
     */
    function watchSession() {
        const sessionState = sessionStoreStrategy.retrieveSession();
        if ((0, sessionState_1.isSessionInExpiredState)(sessionState)) {
            (0, sessionStoreOperations_1.processSessionStoreOperations)({
                process: (sessionState) => (0, sessionState_1.isSessionInExpiredState)(sessionState) ? (0, sessionState_1.getExpiredSessionState)(sessionState, configuration) : undefined,
                after: synchronizeSession,
            }, sessionStoreStrategy);
        }
        else {
            synchronizeSession(sessionState);
        }
    }
    function synchronizeSession(sessionState) {
        if ((0, sessionState_1.isSessionInExpiredState)(sessionState)) {
            sessionState = (0, sessionState_1.getExpiredSessionState)(sessionState, configuration);
        }
        if (hasSessionInCache()) {
            if (isSessionInCacheOutdated(sessionState)) {
                expireSessionInCache();
            }
            else {
                sessionStateUpdateObservable.notify({ previousState: sessionCache, newState: sessionState });
                sessionCache = sessionState;
            }
        }
        return sessionState;
    }
    function startSession() {
        (0, sessionStoreOperations_1.processSessionStoreOperations)({
            process: (sessionState) => {
                if ((0, sessionState_1.isSessionInNotStartedState)(sessionState)) {
                    return (0, sessionState_1.getExpiredSessionState)(sessionState, configuration);
                }
            },
            after: (sessionState) => {
                sessionCache = sessionState;
            },
        }, sessionStoreStrategy);
    }
    function expandOrRenewSessionState(sessionState) {
        if ((0, sessionState_1.isSessionInNotStartedState)(sessionState)) {
            return false;
        }
        const trackingType = computeTrackingType(sessionState[productKey]);
        sessionState[productKey] = trackingType;
        delete sessionState.isExpired;
        if (trackingType !== sessionConstants_1.SESSION_NOT_TRACKED && !sessionState.id) {
            sessionState.id = (0, stringUtils_1.generateUUID)();
            sessionState.created = String((0, timeUtils_1.dateNow)());
        }
    }
    function hasSessionInCache() {
        return (sessionCache === null || sessionCache === void 0 ? void 0 : sessionCache[productKey]) !== undefined;
    }
    function isSessionInCacheOutdated(sessionState) {
        return sessionCache.id !== sessionState.id || sessionCache[productKey] !== sessionState[productKey];
    }
    function expireSessionInCache() {
        sessionCache = (0, sessionState_1.getExpiredSessionState)(sessionCache, configuration);
        expireObservable.notify();
    }
    function renewSessionInCache(sessionState) {
        sessionCache = sessionState;
        renewObservable.notify();
    }
    function updateSessionState(partialSessionState) {
        (0, sessionStoreOperations_1.processSessionStoreOperations)({
            process: (sessionState) => ({ ...sessionState, ...partialSessionState }),
            after: synchronizeSession,
        }, sessionStoreStrategy);
    }
    return {
        expandOrRenewSession: throttledExpandOrRenewSession,
        expandSession,
        getSession: () => sessionCache,
        renewObservable,
        expireObservable,
        sessionStateUpdateObservable,
        restartSession: startSession,
        expire: () => {
            cancelExpandOrRenewSession();
            sessionStoreStrategy.expireSession(sessionCache);
            synchronizeSession((0, sessionState_1.getExpiredSessionState)(sessionCache, configuration));
        },
        stop: () => {
            (0, timer_1.clearInterval)(watchSessionTimeoutId);
        },
        updateSessionState,
    };
}
//# sourceMappingURL=sessionStore.js.map